import apiService from "../services/APIService";
import Response from "../modules/classes/Response";
import { FEED_TYPES } from "../modules/Enumeration";
import Post from "../models/Post";

class PostController {
    constructor(apiService) {
        this.apiService = apiService;
    }

    createPost(postData) {
        return this.apiService.requestWithRawJson('/posts', 'POST', postData);
    }

    updatePost(postID, postData) {
        return this.apiService.requestWithRawJson(`/posts/${postID}`, 'PATCH', postData);
    }

    validatePostData(postData) {
        const errors = {};

        if (!postData.caption) {
            errors.caption = 'Caption is required';
        }

        const data = {
            caption: postData.caption,
            media: postData.media,
            background: postData.background,
            publicity: postData.publicity,
            hashtags: postData.hashtags,
            stickers: postData.stickers,
            displayOrder: postData.displayOrder
        };

        return { errors, data };
    }

    async likePost(postId) {
        return new Promise((resolve, reject) => {
            this.apiService.post(`/posts/${postId}/like`).then((res) => {
                resolve(res.data);
            }).catch((err) => {
                reject(err);
            });
        });
    }

    async unlikePost(postId) {
        return new Promise((resolve, reject) => {
            this.apiService.delete(`/posts/${postId}/like`).then((res) => {
                resolve(res.data);
            }).catch((err) => {
                reject(err);
            });
        });
    }

    async savePost(postId) {
        return new Promise((resolve, reject) => {
            this.apiService
                .requestWithRawJson(`/posts/${postId}/save`, 'POST', {})
                .then((res) => {
                    resolve(res.data.saved);
                })
                .catch((err) => {
                    console.log('Save Post Error:', err);
                    reject(err);
                });
        });
    }

    async unsavePost(postId) {
        return new Promise((resolve, reject) => {
            this.apiService.delete(`/posts/${postId}/save`).then((res) => {
                resolve(res.data.saved);
            }).catch((err) => {
                reject(err);
            });
        });
    }

    async deletePost(postId) {
        return new Promise((resolve, reject) => {
            this.apiService.delete(`/posts/${postId}`).then((res) => {
                resolve(res.data.deleted);
            }).catch((err) => {
                reject(err);
            });
        });
    }

    async pinPost(postId) {
        return new Promise((resolve, reject) => {
            this.apiService.post(`/posts/${postId}/pin`).then((res) => {
                resolve(res.data);
            }).catch((err) => {
                console.log('Pin Post Error:', err);
                reject(err);
            });
        });
    }

    async unpinPost() {
        return new Promise((resolve, reject) => {
            this.apiService.delete(`/posts/pin`).then((res) => {
                resolve(res.data);
            }).catch((err) => {
                console.log('Unpin Post Error:', err);
                reject(err);
            });
        });
    }

    // Comment aliases for backward compatibility
    createComment(postId, commentData) {
        return this.apiService
            .requestWithRawJson(`/posts/${postId}/comment`, 'POST', commentData)
            .then((res) => res.data);
    }

    getParentComments(postId, skip = 0, take = 10) {
        return this.apiService
            .requestWithParams(`/posts/${postId}/comment`, 'GET', { skip, take })
            .then((res) => res.data);
    }

    likeComment(postId, commentId) {
        return this.apiService
            .post(`/posts/${postId}/comment/${commentId}/like`)
            .then((res) => res.data.like);
    }

    unlikeComment(postId, commentId) {
        return this.apiService
            .delete(`/posts/${postId}/comment/${commentId}/like`)
            .then((res) => res.data.like);
    }

    async sharePost(postId, postData) {
        return new Promise((resolve, reject) => {
            this.apiService.requestWithRawJson(`/posts/${postId}/share`, 'POST', postData).then((res) => {
                resolve(res.data);
            }).catch((err) => {
                reject(err);
            });
        });
    }

    getPost(postId) {
        return this.apiService.get(`/posts/${postId}`).then((res) => {
            return res.data;
        });
    }

    getPosts() {
        return this.apiService.get('/posts');
    }

    getPostsByUser(userId) {
        return this.apiService.get(`/posts/user/${userId}`);
    }

    getPostsByHashtag(hashtag) {
        return this.apiService.get(`/posts/hashtag/${hashtag}`);
    }

    getPostsBySticker(sticker) {
        return this.apiService.get(`/posts/sticker/${sticker}`);
    }

    getPostsByPublicity(publicity) {
        return this.apiService.get(`/posts/publicity/${publicity}`);
    }

    getPostsByDate(date) {
        return this.apiService.get(`/posts/date/${date}`);
    }

    getPostsBySearch(query) {
        return this.apiService.get(`/posts/search?q=${query}`);
    }

    voteOnPoll(postId, votes) {
        return this.apiService.requestWithRawJson(`/posts/${postId}/poll/vote`, 'POST', votes).then((res) => {
            console.log("Vote On Poll:", res)
            return res.data;
        }).catch((err) => {
            console.log(err);
            throw err;
        });
    }

    getSavedPosts() {
        return this.apiService.get('/posts/saved').then((res) => {
            if (res.statusCode == 200) {
                return res.data.map((post) => post)
            }

            return [];
        });
    }

    async archivePost(postId) {
        return new Promise((resolve, reject) => {
            this.apiService.post(`/posts/${postId}/archive`).then((res) => {
                resolve(res.data);
            }).catch((err) => {
                reject(err);
            });
        });
    }

    async unarchivePost(postId) {
        return new Promise((resolve, reject) => {
            this.apiService.delete(`/posts/${postId}/archive`).then((res) => {
                resolve(res.data);
            }).catch((err) => {
                reject(err);
            });
        });
    }

    async getArchivedPosts(skip = 0, take = 20) {
        return this.apiService.requestWithParams('/posts/archived', 'GET', { skip, take }).then((res) => {
            if (res.statusCode === 200) {
                return {
                    posts: res.data.posts.map((post) => Post.fromAPI(post)),
                    total: res.data.total,
                    page: res.data.page,
                    totalPages: res.data.totalPages
                };
            }
            return { posts: [], total: 0, page: 1, totalPages: 0 };
        }).catch((err) => {
            console.error('Error fetching archived posts:', err);
            return { posts: [], total: 0, page: 1, totalPages: 0 };
        });
    }

    // ================= Reactions (emoji-based) =================
    async addPostReaction(postId, emoji) {
        return this.apiService.requestWithRawJson(`/posts/${postId}/reactions`, 'POST', { emoji })
            .then((res) => res.data)
            .catch((err) => { throw err; });
    }

    async removePostReaction(postId, emoji) {
        return this.apiService.requestWithParams(`/posts/${postId}/reactions`, 'DELETE', { emoji })
            .then((res) => res.data)
            .catch((err) => { throw err; });
    }

    async getPostReactions(postId) {
        return this.apiService.requestWithParams(`/posts/${postId}/reactions`, 'GET')
            .then((res) => res.data)
            .catch((err) => { throw err; });
    }

    async addCommentReaction(postId, commentId, emoji) {
        return this.apiService.requestWithRawJson(`/posts/${postId}/comments/${commentId}/reactions`, 'POST', { emoji })
            .then((res) => res.data)
            .catch((err) => { throw err; });
    }

    async removeCommentReaction(postId, commentId, emoji) {
        return this.apiService.requestWithParams(`/posts/${postId}/comments/${commentId}/reactions`, 'DELETE', { emoji })
            .then((res) => res.data)
            .catch((err) => { throw err; });
    }
}

export class CommentController {
    constructor(apiService) {
        this.apiService = apiService;
    }
    createComment(postId, commentData) {
        // commentData can include: { content, parentId?, mediaUrl?, gifUrl? }
        return this.apiService.requestWithRawJson(`/posts/${postId}/comment`, 'POST', commentData).then((res) => {
            return res.data;
        }).catch((err) => {
            console.log(err);
            throw err;
        });
    }

    getComments(postId, take = 10, skip = 0) {
        return this.apiService.requestWithParams(`/posts/${postId}/comment`, 'GET', { take, skip }).then((res) => {
            return res.data;
        }).catch((err) => {
            console.log(err);
            throw err;
        });
    }

    toggleLikeComment(commentId) {
        return this.apiService.post(`/posts/comment/${commentId}/like`);
    }

    likeComment(postId, commentId) {
        return this.apiService.post(`/posts/${postId}/comment/${commentId}/like`).then((res) => {
            return res.data.like;
        }).catch((err) => {
            console.log(err);
            throw err;
        });
    }

    unlikeComment(postId, commentId) {
        return this.apiService.delete(`/posts/${postId}/comment/${commentId}/like`).then((res) => {
            return res.data.like;
        }).catch((err) => {
            console.log(err);
            throw err;
        });
    }

    // Added for parity: edit and delete comment
    updateComment(postId, commentId, commentData) {
        return this.apiService.requestWithRawJson(`/posts/${postId}/comment/${commentId}`, 'PATCH', commentData)
            .then((res) => res.data)
            .catch((err) => { console.log(err); throw err; });
    }

    deleteComment(postId, commentId) {
        return this.apiService.delete(`/posts/${postId}/comment/${commentId}`)
            .then((res) => res.data)
            .catch((err) => { console.log(err); throw err; });
    }

    // Replies listing (for future nesting support)
    getReplies(postId, commentId, take = 10, skip = 0) {
        return this.apiService.requestWithParams(`/posts/${postId}/comment/${commentId}/replies`, 'GET', { take, skip })
            .then((res) => res.data)
            .catch((err) => { console.log(err); throw err; });
    }
}

export class FeedController {
    constructor(apiService) {
        this.apiService = apiService;
        this.take = 10;
        this.nextCursor = null;
        this.previousCursor = null;
        this.isFetching = false;
        this.posts = [];
        this.type = FEED_TYPES.FOLLOWING;
        this.targetUser = null;
    }

    getService() {
        return this.apiService ? this.apiService : apiService;
    }

    async getFeed(take = 10, cursor = null, type = FEED_TYPES.FOLLOWING, targetUser = null, publicFeed = false) {
        const data = {};

        if (take) data.take = take;
        if (cursor) data.cursor = cursor;
        if (type) data.feedType = type;

        if (type && type === FEED_TYPES.PROFILE) {
            if (targetUser) data.targetUser = targetUser;   
        }

        if (type && type === FEED_TYPES.COMPANY) {
            if (targetUser) data.companyId = targetUser;  
        }

        if (type && type === FEED_TYPES.GROUP) {
            if (targetUser) data.groupId = targetUser;   
        }

        this.isFetching = true;
        this.type = type;
        this.targetUser = targetUser;

        return this.getService().requestWithParams(`${publicFeed ? '/posts/public' : '/posts/feed'}`, 'GET', data).then((res) => {
            // Support both shapes:
            // 1) { statusCode, data: { posts, nextCursor, previousCursor } }
            // 2) { posts, nextCursor, previousCursor }

            const payload = res && (res.data ? res.data : res);
            if (!payload) return [];

            this.isFetching = false;

            const posts = payload.posts || [];
            
            this.posts = posts;
            this.nextCursor = payload.nextCursor || null;
            this.previousCursor = payload.previousCursor || null;

            const mappedPosts = posts.map((post) => Post.fromAPI(post)).filter(p => p !== null);
            
            return mappedPosts;
        }).catch((err) => {
            this.isFetching = false;

            return [];
        });
    }

    async loadMore() {
        if (this.isFetching) return;

        return this.getFeed(this.take, this.nextCursor);
    }

    /**
     * Vote on a poll
     * @param {string} pollId - Poll ID
     * @param {Object} voteData - Vote data { userId, pollId, votes: [pollValueId1, pollValueId2, ...] }
     * @returns {Promise<Object>}
     */
    async voteOnPoll(pollId, voteData) {
        return this.getService().requestWithRawJson(`/posts/polls/${pollId}/vote`, 'POST', voteData)
            .then((res) => {
                // Support both shapes:
                // 1) apiSuccess: { statusCode, data, message }
                // 2) raw payload
                return res && res.data ? res.data : res;
            })
            .catch((err) => {
                console.error('Error voting on poll:', err);
                throw err;
            });
    }
}

export class HashtagController {
    constructor(apiService) {
        this.apiService = apiService;
    }

    getService() {
        return this.apiService ? this.apiService : apiService;
    }

    async getTrendingHashtags(limit = 10) {
        return this.getService().requestWithParams('/posts/hashtags/trending', 'GET', { limit })
            .then((res) => {
                return res;
            })
            .catch((err) => {
                console.error('Error fetching trending hashtags:', err);
                return { data: [] };
            });
    }

    async searchHashtags(query, limit = 20, skip = 0) {
        return this.getService().requestWithParams(`/posts/hashtags/search/${encodeURIComponent(query)}`, 'GET', { limit, skip })
            .then((res) => {
                // Keep apiSuccess shape for callers that expect { data: [...] }
                return res;
            })
            .catch((err) => {
                console.error('Error searching hashtags:', err);
                return { data: [] };
            });
    }

    async getPostsByHashtag(tag, skip = 0, take = 10) {
        return this.getService().requestWithParams(`/posts/hashtags/${encodeURIComponent(tag)}`, 'GET', { skip, take })
            .then((res) => {
                if (!res.data) return { posts: [], hashtag: tag, postCount: 0 };
                return res.data;
            })
            .catch((err) => {
                console.error('Error fetching posts by hashtag:', err);
                return { posts: [], hashtag: tag, postCount: 0 };
            });
    }
}

export default PostController;
